import { defineComponent as _defineComponent } from 'vue'
import BreadCrumbComponent from "@/components/bread-crumb-component.vue";
import { ref, Ref } from "vue";
import useHttpRequest from "@/composables/use-http-request";
import AwsUploadUrl from "@/interfaces/awsUploadUrl.interface";
import { useShowWarningMessage } from "@/composables/use-notifications";
import axios from "axios";
import store from "@/store";
import { useRoute, useRouter } from "vue-router/composables";
import { Loading } from "element-ui";
import { ElInputFile } from "@/interfaces/elInputFie.interface";

interface IntakeGroupSearchResult {
  course_intake_batch_group_id: string;
  label: string;
}

interface ModuleSearchResult {
  course_unit_intake_batch_combined_id: string;
  course_unit_with_intake: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'add-course-work',
  setup(__props) {

const route = useRoute();
const router = useRouter();
const isSearching = ref(false);

const totalMarks = ref("");
const courseWorkTitle = ref("");
const courseWorkInstructions = ref("");
const startTime = ref("");
const endTime = ref("");
const isFileUploadAllowed = ref(false);

const chosenCourseUnitsWithIntakes = ref([] as string[]);
const chosenIntakeGroups = ref([] as string[]);
const courseUnitsWithCourseIntakeBatches = ref<ModuleSearchResult[]>([]);
const intakeGRoupSearchResults = ref<IntakeGroupSearchResult[]>([]);
const courseWorkQuestionFiles = ref<ElInputFile[]>([]);
const answerSheetsFiles = ref<ElInputFile[]>([]);
const selectedAnswerFile: Ref<ElInputFile | null> = ref(null);
const selectedQuestionFile: Ref<ElInputFile | null> = ref(null);

const onSearchModules = async (query: string) => {
  if (query !== "" && query.length >= 3) {
    const request = await useHttpRequest<{
      success: boolean;
      message: string;
      search_results: ModuleSearchResult[];
    }>({
      method: "GET",
      url: `course-units/search-course-unit?search_query=${query}&lecturer_id=${store.state.userId}`,
      loadingProperty: isSearching,
      showSuccessMessage: false,
    });

    if (!request) return;

    if (
      request.success &&
      request.message == "SEARCH RESULTS RETRIEVED SUCCESSFULLY"
    ) {
      courseUnitsWithCourseIntakeBatches.value = request.search_results;
    }
  }
};

const onSearchIntakeGroups = async (query: string) => {
  if (!chosenCourseUnitsWithIntakes.value.length) {
    return useShowWarningMessage(
      "Modules Required",
      "Please select at least one module before searching for intake groups"
    );
  }
  if (query !== "" && query.length >= 3) {
    const request = await useHttpRequest<{
      success: boolean;
      message: string;
      results: IntakeGroupSearchResult[];
    }>({
      method: "POST",
      url: `course-intake-batches/intake-groups/search?query=${query}`,
      loadingProperty: isSearching,
      showSuccessMessage: false,
      body: {
        course_intake_batch_ids: chosenCourseUnitsWithIntakes.value.map(
          (item) => item.split("_")[0]
        ),
      },
    });

    if (!request) return;

    if (
      request.success &&
      request.message == "Intake groups retrieved successfully"
    ) {
      intakeGRoupSearchResults.value = request.results;
    }
  }
};

const handleQuestionPreview = (file: ElInputFile) => {
  courseWorkQuestionFiles.value = [file];
  selectedQuestionFile.value = file;
};

const handleQuestionRemove = () => {
  selectedQuestionFile.value = null;
  courseWorkQuestionFiles.value = [];
};

const handleAnswerPreview = (file: ElInputFile) => {
  answerSheetsFiles.value = [file];
  selectedAnswerFile.value = file;
};

const handleAnswerRemove = () => {
  selectedAnswerFile.value = null;
  answerSheetsFiles.value = [];
};

const requestUploadUrls = async () => {
  const request = await useHttpRequest<{
    success: boolean;
    message: string;
    question_paper: { upload_url: AwsUploadUrl; path_key: string };
    answer_sheet: { upload_url: AwsUploadUrl; path_key: string } | null;
  }>({
    method: "POST",
    url: "course-work/request-for-file-upload-urls",
    loadingProperty: null,
    showSuccessMessage: false,
    body: {
      question_paper: {
        file_name: selectedQuestionFile.value?.raw?.name,
        file_size: selectedQuestionFile.value?.raw?.size,
        mime_type: selectedQuestionFile.value?.raw?.type,
      },
      ...(selectedAnswerFile.value && {
        answer_sheet: {
          file_name: selectedAnswerFile.value?.raw?.name,
          file_size: selectedAnswerFile.value?.raw?.size,
        },
      }),
    },
  });

  if (!request) return;
  if (request.success && request.message == "UPLOAD URLS GENERATED") {
    return {
      question_paper: request.question_paper,
      answer_sheet: request.answer_sheet,
    };
  }
};

const uploadResourceToAWS = async ({
  awsUploadUrl,
  selectedFile,
  onProgress,
}: {
  awsUploadUrl: AwsUploadUrl;
  selectedFile: File;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onProgress: (progressEvent: ProgressEvent) => void;
}) => {
  try {
    const formData = new FormData();
    Object.entries(awsUploadUrl.fields).forEach(([key, value]) => {
      //@ts-ignore
      formData.append(key, value);
    });
    formData.append("file", selectedFile);

    // send the file to the aws server
    const response = await axios.post(`${awsUploadUrl.url}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: " ",
      },
      onUploadProgress: onProgress,
    });

    if (response.status == 204) {
      return response.headers.location;
    } else {
      throw "UNEXPECTED_RESPONSE";
    }
  } catch (error: any) {
    if (error.message == "Network Error") {
      useShowWarningMessage(
        "Connection failed",
        "A network error occurred, please try again."
      );
    }
    useShowWarningMessage(
      "Upload Failed",
      "Unable to Upload Resource Now, please try again"
    );
  }
};

const addCourseWork = async ({
  answerSheetPathUrl,
  questionPaperPathUrl,
  uploadUrls,
}: {
  questionPaperPathUrl: string;
  answerSheetPathUrl: string;
  uploadUrls: Awaited<ReturnType<typeof requestUploadUrls>>;
}) => {
  const request = await useHttpRequest<{ success: boolean; message: string }>({
    method: "POST",
    url: "course-work/add",
    loadingProperty: null,
    showSuccessMessage: true,
    body: {
      course_work_set_id: route.params.courseWorkSetId,
      title: courseWorkTitle.value,
      total_marks: totalMarks.value,
      question_paper_path_url: questionPaperPathUrl,
      question_paper_path_key: uploadUrls?.question_paper.path_key,
      answer_sheet_path_url: selectedAnswerFile.value
        ? answerSheetPathUrl
        : undefined,
      answer_sheet_path_key: uploadUrls?.answer_sheet
        ? uploadUrls.answer_sheet.path_key
        : undefined,
      available_from: startTime.value,
      available_upto: endTime.value,
      instructions: courseWorkInstructions.value,
      question_paper_mime_type: selectedQuestionFile.value?.raw?.type,
      course_units_and_batches: chosenCourseUnitsWithIntakes.value.map(
        (item) => {
          /* Since the Id is intentionally concatenated with the batch and course unit separated by an underscore
            since a course work can be on the same intake but multiple course units, it causes vue v-for duplication errors
            but the combination of the intake and course unit is always unique
            we split it before we send to the API */
          const itemParts = item.split("_");
          return {
            course_intake_batch_id: itemParts[0],
            course_unit_id: itemParts[1],
          };
        }
      ),
      intake_group_ids: chosenIntakeGroups.value,
      is_answersheet_file_upload_allowed: isFileUploadAllowed.value,
    },
  });

  if (!request) return;
  if (request.success && request.message == "Course Work added successfully") {
    router.push({
      path: `/dashboard/course-work/${route.params.courseWorkSetId}`,
    });
  }
};

const validateInput = () => {
  if (!chosenCourseUnitsWithIntakes.value.length) {
    return useShowWarningMessage(
      "Modules Required",
      "Modules for this Course Work are required"
    );
  }

  if (!courseWorkTitle.value) {
    return useShowWarningMessage(
      "Title Required",
      "The Title for this Course Work are required"
    );
  }
  if (courseWorkTitle.value.length > 255) {
    return useShowWarningMessage(
      "Title too long",
      "The Title for this Course Work is too long. It should be less than 255 Characters"
    );
  }

  if (!totalMarks.value) {
    return useShowWarningMessage(
      "Course Work Total Marks Required",
      "The Total Marks for this course work is required"
    );
  }
  if (
    totalMarks.value &&
    (isNaN(+totalMarks.value) ||
      +totalMarks.value > 100 ||
      +totalMarks.value <= 0)
  ) {
    return useShowWarningMessage(
      "Total Marks Invalid",
      "Total marks should be a number between 0 and 100"
    );
  }

  if (!startTime.value || !endTime.value) {
    return useShowWarningMessage(
      "Start & End Time Required",
      "The Start & End Time are both Required "
    );
  }

  if (+startTime.value >= +endTime.value) {
    return useShowWarningMessage(
      "Invalid Time",
      "The End Time can not be before or equal to the Start Time, please review it and try again."
    );
  }

  if (
    courseWorkInstructions.value &&
    courseWorkInstructions.value.length > 1000
  ) {
    return useShowWarningMessage(
      "Instructions too Long",
      "Course Work Instructions are too long. It should be less than 1000 Characters"
    );
  }

  if (!selectedQuestionFile.value) {
    return useShowWarningMessage(
      "Question Paper is required",
      "A Question Paper is required for this Course Work."
    );
  }

  if ((selectedQuestionFile.value?.raw?.size ?? 0) > 100 * 1024 * 1024) {
    return useShowWarningMessage(
      "File Too Big",
      "The Question Paper File you are trying to upload is too Big. Please choose another one below 100Mbs."
    );
  }

  //If answer sheet is supplied, Ensure it is not more than 100Mbs
  if (
    selectedAnswerFile.value &&
    (selectedAnswerFile.value?.size ?? 0) > 100 * 1024 * 1024
  ) {
    return useShowWarningMessage(
      "File Too Big",
      "The Answer Sheet File you are trying to upload is too Big. Please choose another one below 100Mbs."
    );
  }
  return true;
};

const onSubmit = async () => {
  let loadingIndicator;
  try {
    if (validateInput() !== true) {
      return;
    }

    loadingIndicator = Loading.service({
      lock: true,
      text: "Please wait...",
      background: "rgba(0, 0, 0, 0.7)",
    });

    const uploadUrls = await requestUploadUrls();

    if (!uploadUrls) {
      return useShowWarningMessage(
        "Upload Failed",
        "Unable to Upload Resource Now, please try again"
      );
    }

    //Since we have got both URLs, we then need to upload the Question Paper first
    const questionPaperPathUrl = await uploadResourceToAWS({
      awsUploadUrl: uploadUrls.question_paper.upload_url,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      selectedFile: selectedQuestionFile.value?.raw!,
      onProgress: (progressEvent: ProgressEvent) => {
        const loader = document.querySelector(
          ".el-loading-text"
        ) as HTMLElement;

        const progress = +(
          (progressEvent.loaded / progressEvent.total) *
          100
        ).toFixed(0);

        loader.innerText = `Uploading Question Paper... ${progress}%`;
      },
    });

    //If the user chose an answer sheet too, upload it
    const answerSheetPathUrl = selectedAnswerFile.value
      ? await uploadResourceToAWS({
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          awsUploadUrl: uploadUrls.answer_sheet?.upload_url!,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          selectedFile: selectedAnswerFile.value.raw!,
          onProgress: (progressEvent: ProgressEvent) => {
            const loader = document.querySelector(
              ".el-loading-text"
            ) as HTMLElement;

            const progress = +(
              (progressEvent.loaded / progressEvent.total) *
              100
            ).toFixed(0);

            loader.innerText = `Uploading Answer sheet... ${progress}%`;
          },
        })
      : null;

    await addCourseWork({
      questionPaperPathUrl,
      answerSheetPathUrl,
      uploadUrls,
    });
  } catch (error) {
    useShowWarningMessage("Error", "An error occurred while uploading files");
  } finally {
    loadingIndicator?.close();
  }
};

return { __sfc: true,route, router, isSearching, totalMarks, courseWorkTitle, courseWorkInstructions, startTime, endTime, isFileUploadAllowed, chosenCourseUnitsWithIntakes, chosenIntakeGroups, courseUnitsWithCourseIntakeBatches, intakeGRoupSearchResults, courseWorkQuestionFiles, answerSheetsFiles, selectedAnswerFile, selectedQuestionFile, onSearchModules, onSearchIntakeGroups, handleQuestionPreview, handleQuestionRemove, handleAnswerPreview, handleAnswerRemove, requestUploadUrls, uploadResourceToAWS, addCourseWork, validateInput, onSubmit, BreadCrumbComponent }
}

})